// Dominio del modo "Mostrar cómo funciona" — video corto (5-8 s) del mueble en uso,
// sólo para marcas del rubro Hogar y Decoración (ADR-2026-09-03-modo-mostrar-como-funciona).
//
// Vive aparte de la pantalla (`modes/MostrarComoFuncionaMode.tsx`) por lo mismo que
// `vistasMuebles.ts` vive aparte del picker: son las listas que decide producto —qué
// acciones existen, qué ambientes se ofrecen, cuánto dura y cuánto cuesta— y cambiarlas
// no debería obligar a abrir el componente. Cuando exista backend, los `id` de acá son
// los que van a viajar en el payload.

/** Qué pasa en el video. Es LA decisión propia del modo: el resto la afina. */
export type AccionVideoId = 'usa' | 'recorre' | 'transforma';

export interface AccionVideo {
  id: AccionVideoId;
  nombre: string;
  /** La bajada que aparece debajo de las tres opciones cuando ésta está elegida. */
  descripcion: string;
}

export const ACCIONES_VIDEO: AccionVideo[] = [
  {
    id: 'usa',
    nombre: 'Alguien lo usa',
    descripcion:
      'Una persona se sienta, apoya la taza y sale de cuadro. La cara nunca es el foco.',
  },
  {
    id: 'recorre',
    nombre: 'La cámara recorre',
    descripcion:
      'Un paneo lento por el ambiente que termina en el mueble. Sin personas.',
  },
  {
    id: 'transforma',
    nombre: 'Se transforma',
    descripcion:
      'El mueble cambia de estado: un sofá cama que se abre, una mesa que se extiende.',
  },
];

export const accionVideoPorId = (id: AccionVideoId): AccionVideo =>
  ACCIONES_VIDEO.find((a) => a.id === id) ?? ACCIONES_VIDEO[0];

/**
 * Dónde pasa. Lista FIJA y no la biblioteca de fondos: el brief pide "tipo de ambiente"
 * (living, comedor, dormitorio, balcón), y la biblioteca mezcla fondos de moda con un
 * mueble. Cada uno tiene su foto de ejemplo en `/images/ambientes/`; el ícono de la
 * grilla queda como fallback por si a alguno le falta el asset.
 */
export type AmbienteId = 'living' | 'comedor' | 'dormitorio' | 'balcon' | 'estudio';

export interface Ambiente {
  id: AmbienteId;
  nombre: string;
  /** Lo que se ve alrededor del mueble — la luz y los objetos que arman la escena. */
  descripcion: string;
  imageUrl?: string;
}

export const AMBIENTES: Ambiente[] = [
  {
    id: 'living',
    nombre: 'Living',
    descripcion: 'Luz de tarde, alfombra y una planta.',
    imageUrl: '/images/ambientes/living.png',
  },
  {
    id: 'comedor',
    nombre: 'Comedor',
    descripcion: 'Mesa puesta, luz cálida de techo.',
    imageUrl: '/images/ambientes/comedor.png',
  },
  {
    id: 'dormitorio',
    nombre: 'Dormitorio',
    descripcion: 'Luz de mañana, textiles claros.',
    imageUrl: '/images/ambientes/dormitorio.png',
  },
  {
    id: 'balcon',
    nombre: 'Balcón',
    descripcion: 'Exterior, sol pleno y verde de fondo.',
    imageUrl: '/images/ambientes/balcon.png',
  },
  {
    id: 'estudio',
    nombre: 'Estudio',
    descripcion: 'Escritorio, biblioteca y luz neutra.',
    imageUrl: '/images/ambientes/estudio.png',
  },
];

export const ambientePorId = (id: AmbienteId): Ambiente =>
  AMBIENTES.find((a) => a.id === id) ?? AMBIENTES[0];

/**
 * La sexta opción de "Dónde pasa": el ambiente lo ESCRIBE el usuario
 * (ADR-2026-09-04-donde-pasa-tiene-una-opcion-para-describirlo).
 *
 * Vive aparte de `AMBIENTES` a propósito: los cinco de la lista son escenas curadas con
 * su luz decidida, y éste no es una escena sino un permiso. Metido en la lista, cualquier
 * recorrido de ambientes (la grilla, un futuro `imageUrl`, el payload) tendría que
 * acordarse de saltearlo.
 */
export const AMBIENTE_PROPIO_ID = 'propio' as const;

export const AMBIENTE_PROPIO = {
  id: AMBIENTE_PROPIO_ID,
  nombre: 'Lo describo yo',
  descripcion: 'Escribí vos dónde pasa y con qué luz.',
} as const;

/** Lo que puede quedar elegido en "Dónde pasa": uno de los curados, o el propio. */
export type AmbienteElegido = AmbienteId | typeof AMBIENTE_PROPIO_ID;

/** Duración del video, en segundos. El brief dice 5-8. */
export const DURACION_MIN = 5;
export const DURACION_MAX = 8;
export const DURACION_DEFAULT = 6;

/**
 * Créditos por segundo. Igual que Animar imagen (`creditosPorUnidad: 1`,
 * `unidadConsumo: 'segundo'` en `modesCatalog`), que es el otro modo que saca video de
 * una foto. Cuando haya backend, la cifra tiene que salir de él y no de acá.
 */
export const CREDITOS_POR_SEGUNDO = 1;

export const costoVideo = (duracionSeg: number): number =>
  duracionSeg * CREDITOS_POR_SEGUNDO;

/**
 * Proporción ancho/alto de cada formato, para dibujar el recuadro de la tanda con la
 * forma de lo que va a salir. Las claves son los `id` de `FORMAT_OPTIONS_V2`.
 */
export const RATIO_POR_FORMATO: Record<string, number> = {
  post: 4 / 5,
  story: 9 / 16,
  square: 1,
  portrait: 2 / 3,
  banner: 16 / 9,
  editorial: 3 / 2,
  billboard: 21 / 9,
};

export const ratioDeFormato = (formatoId: string | null): number =>
  (formatoId && RATIO_POR_FORMATO[formatoId]) || 1;

/** Un extremo de "Se transforma": la foto de cómo empieza o de cómo termina. */
export interface ExtremoTransformacion {
  /** URL para dibujar la miniatura (signed URL de la creación u `objectURL` de un archivo). */
  url: string;
  /** Nombre a mostrar debajo de la miniatura. */
  nombre: string;
  /**
   * Es la FOTO DE PARTIDA del modo, no un archivo subido para este extremo. Se llamaba
   * `esDelProducto` cuando el modo arrancaba de un producto del catálogo
   * (ADR-2026-09-04-mostrar-como-funciona-parte-de-una-creacion).
   */
  esDeLaFoto: boolean;
  /** El archivo, cuando lo subió el usuario. Es lo que viajaría al backend. */
  archivo?: File;
}
